import type { Client, ClientBulkAction } from '@/admin/api/clients';
import { clientsApi } from '@/admin/api/clients';
import { demoApi } from '@/admin/api/demo';
import {
	getAvatarColor,
	getInitials,
} from '@/admin/pages/clients/client-detail.utils';
import { clientsQuery } from '@/admin/queries/clients';
import { ordersQuery } from '@/admin/queries/orders';
import { DataTable } from '@/components/table';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BulkActionBar } from '@/components/ui/bulk-action-bar';
import { Button, buttonVariants } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from '@/components/ui/input-group';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { dateI18n, getSettings } from '@wordpress/date';
import { __, sprintf } from '@wordpress/i18n';
import {
	EyeIcon,
	MoreVertical,
	Pencil,
	Plus,
	SearchIcon,
	Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import ClientDialog from './client-dialog';

const routeApi = getRouteApi('/_app/clients/');

const col = createColumnHelper<Client>();

const Clients = () => {
	const queryClient = useQueryClient();
	const navigate = routeApi.useNavigate();
	const search = routeApi.useSearch();
	const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
	const [selectedClients, setSelectedClients] = useState<Client[]>([]);
	const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
	const [resetSelectionKey, setResetSelectionKey] = useState(0);
	const [searchInput, setSearchInput] = useState(search.search);
	const debouncedSearch = useDebouncedValue(searchInput);

	// Sync input when URL changes externally (e.g. browser back/forward)
	useEffect(() => {
		setSearchInput(search.search);
	}, [search.search]);

	// Update URL after debounce
	useEffect(() => {
		if (debouncedSearch !== search.search) {
			setFilter({ search: debouncedSearch });
		}
	}, [debouncedSearch]);

	const setFilter = (updates: Partial<typeof search>) => {
		navigate({ search: { ...search, ...updates, page: 1 } });
	};

	const { data, isLoading, isFetching } = useSuspenseQuery(
		clientsQuery({
			page: search.page,
			per_page: search.per_page,
			search: debouncedSearch || undefined,
		}),
	);

	const { data: ordersData } = useQuery(ordersQuery({ per_page: 100 }));

	const clients = data?.clients ?? [];
	const total = data?.total ?? 0;

	const ordersCountMap = useMemo(() => {
		const map = new Map<number, number>();
		for (const b of ordersData?.orders ?? []) {
			map.set(b.client_id, (map.get(b.client_id) ?? 0) + 1);
		}
		return map;
	}, [ordersData]);

	const deleteClient = useMutation({
		mutationFn: (id: number) => clientsApi.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['clients'] });
			toast.success(__('Client deleted.', 'allcoach'));
		},
		onError: () => {
			toast.error(__('Failed to delete client.', 'allcoach'));
		},
	});

	const demoMutation = useMutation({
		mutationFn: demoApi.start,
		mutationKey: ['demo'],
		onSuccess: (data) => {
			window.location.href = data.url;
		},
		onError(e) {
			toast.error(e.message);
		},
	});

	const bulkAction = useMutation({
		mutationFn: ({
			action,
			ids,
		}: {
			action: ClientBulkAction;
			ids: number[];
		}) => clientsApi.bulk(action, ids),
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: ['clients'] });
			setResetSelectionKey((k) => k + 1);
			if (result.failed === 0) {
				toast.success(
					sprintf(
						/* translators: %d: number of clients deleted */
						__('%d client(s) deleted.', 'allcoach'),
						result.processed,
					),
				);
			} else {
				toast.warning(
					sprintf(
						/* translators: %1$d: number of succeeded actions, %2$d: number of failed actions */
						__('%1$d succeeded, %2$d failed.', 'allcoach'),
						result.processed,
						result.failed,
					),
				);
			}
		},
		onError: () => {
			toast.error(__('Bulk action failed. Please try again.', 'allcoach'));
		},
	});

	const columns = [
		col.accessor('id', {
			header: __('ID', 'allcoach'),
			cell: ({ getValue }) => (
				<span className="font-mono text-sm text-gray-500">#{getValue()}</span>
			),
		}),
		col.display({
			id: 'name',
			header: __('Name', 'allcoach'),
			cell: ({ row }) => {
				const avatarColor = getAvatarColor(row.original.name);
				const initials = getInitials(row.original.name);
				return (
					<div className="relative flex items-center gap-3">
						<div
							className={cn(
								'flex size-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-medium',
								avatarColor.bg,
								avatarColor.text,
							)}
						>
							{initials}
						</div>
						<span className="font-semibold text-gray-800">
							{row.original.name}
						</span>
						{row.original.is_demo && (
							<Badge className="text-[10px] uppercase" variant="secondary">
								{__('Demo', 'allcoach')}
							</Badge>
						)}
					</div>
				);
			},
		}),
		col.accessor('email', {
			header: __('Email', 'allcoach'),
			cell: ({ getValue }) => (
				<span className="text-gray-600">{getValue()}</span>
			),
		}),
		col.accessor('phone', {
			header: __('Phone', 'allcoach'),
			cell: ({ getValue }) => (
				<span className="text-gray-600">
					{getValue()?.length ? (
						getValue()
					) : (
						<span className="text-gray-300">—</span>
					)}
				</span>
			),
		}),
		col.accessor('created_at', {
			header: __('Created', 'allcoach'),
			cell: ({ getValue }) => (
				<span className="text-gray-500">
					{dateI18n(getSettings().formats.date, getValue())}
				</span>
			),
		}),
		col.display({
			id: 'total_bookings',
			header: __('Total Orders', 'allcoach'),
			cell: ({ row }) => {
				const count = ordersCountMap.get(row.original.id) ?? 0;
				return <span className="font-medium text-gray-700">{count}</span>;
			},
		}),
		col.display({
			id: 'actions',
			header: () => <div className="w-full text-right">{__('Actions')}</div>,
			cell: ({ row }) => {
				const client = row.original;
				return (
					<div className="flex items-center justify-end gap-1.5">
						{row.original.is_demo && (
							<Button
								variant="outline"
								size="xs"
								className="cursor-pointer text-xs! font-medium!"
								onClick={() => demoMutation.mutate(undefined)}
							>
								<EyeIcon /> {__('Switch to', 'allcoach')}
							</Button>
						)}
						<Button
							variant="outline"
							size="xs"
							className="w-18 cursor-pointer"
							asChild
						>
							<Link
								to="/clients/$clientId"
								params={{
									clientId: client.id.toString(),
								}}
							>
								<Pencil /> {__('Edit', 'allcoach')}
							</Link>
						</Button>
						<DropdownMenu>
							<DropdownMenuTrigger
								className={cn(
									buttonVariants({ variant: 'outline', size: 'icon-xs' }),
									'cursor-pointer',
								)}
							>
								<MoreVertical className="size-4" />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<ClientDialog
									trigger={
										<DropdownMenuItem
											className="cursor-pointer"
											onSelect={(e) => e.preventDefault()}
										>
											<EyeIcon className="size-3.5" />
											{__('Edit', 'allcoach')}
										</DropdownMenuItem>
									}
									editTarget={client}
								/>
								<DropdownMenuItem
									className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600"
									onClick={() => setDeleteTargetId(client.id)}
								>
									<Trash2 className="size-3.5" />
									{__('Delete', 'allcoach')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				);
			},
		}),
	];

	const toolbar = (
		<div className="flex flex-wrap items-center gap-3">
			<div className="w-full md:w-72">
				<InputGroup>
					<InputGroupInput
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						id="clients-search"
						placeholder={__('Search…', 'allcoach')}
					/>
					<InputGroupAddon align="inline-start">
						<SearchIcon className="text-muted-foreground" />
					</InputGroupAddon>
				</InputGroup>
			</div>
			<ClientDialog
				trigger={
					<Button className="w-full cursor-pointer md:ms-auto md:w-auto">
						<Plus />
						{__('Add Client', 'allcoach')}
					</Button>
				}
			/>
		</div>
	);

	return (
		<div className="min-h-screen p-4 md:p-6">
			<DataTable
				data={clients}
				columns={columns}
				enableRowSelection
				toolbar={toolbar}
				isLoading={isLoading}
				isFetching={isFetching}
				rowsPerPageLabel={__('Clients per page', 'allcoach')}
				pageSizeOptions={[10, 25, 50, 100]}
				serverPagination={{
					total,
					page: search.page,
					perPage: search.per_page,
					onPageChange: (page) => navigate({ search: { ...search, page } }),
					onPerPageChange: (perPage) =>
						setFilter({ per_page: perPage as typeof search.per_page }),
				}}
				onSelectionChange={(rows) => setSelectedClients(rows as Client[])}
				resetSelectionKey={resetSelectionKey}
			/>

			{/* Delete confirmation */}
			<AlertDialog
				open={deleteTargetId !== null}
				onOpenChange={(open) => !open && setDeleteTargetId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{__('Delete Client', 'allcoach')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{__(
								'Are you sure you want to delete this client? This action cannot be undone.',
								'allcoach',
							)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel className="cursor-pointer">
							{__('Cancel', 'allcoach')}
						</AlertDialogCancel>
						<AlertDialogAction
							className="cursor-pointer bg-red-600 hover:bg-red-700"
							onClick={() => {
								if (deleteTargetId !== null) {
									deleteClient.mutate(deleteTargetId);
									setDeleteTargetId(null);
								}
							}}
						>
							{__('Delete', 'allcoach')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Bulk delete confirm */}
			<AlertDialog
				open={bulkDeleteConfirm}
				onOpenChange={(open) => !open && setBulkDeleteConfirm(false)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{__('Delete Clients', 'allcoach')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{sprintf(
								/* translators: %d: number of clients to delete */
								__(
									'Are you sure you want to delete %d client(s)? This action cannot be undone.',
									'allcoach',
								),
								selectedClients.length,
							)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel className="cursor-pointer">
							{__('Cancel', 'allcoach')}
						</AlertDialogCancel>
						<AlertDialogAction
							className="cursor-pointer bg-red-600 hover:bg-red-700"
							onClick={() => {
								bulkAction.mutate({
									action: 'delete',
									ids: selectedClients.map((c) => c.id),
								});
								setBulkDeleteConfirm(false);
							}}
						>
							{__('Delete', 'allcoach')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Bulk action bar */}
			<BulkActionBar
				count={selectedClients.length}
				isPending={bulkAction.isPending}
				onDismiss={() => setResetSelectionKey((k) => k + 1)}
				actions={[
					{
						label: __('Delete', 'allcoach'),
						icon: <Trash2 className="size-3.5" />,
						variant: 'danger',
						onClick: () => setBulkDeleteConfirm(true),
					},
				]}
			/>
		</div>
	);
};

export default Clients;
